import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetClose,
} from '@/components/ui/sheet';
import { Menu } from 'lucide-react';
import logo from '@/assets/cannafax-logo-clean.png';
import { cn } from '@/lib/utils';

const links = [
  { href: '/#how', id: 'how', label: 'How it works' },
  { href: '/#private', id: 'private', label: 'Private' },
  { href: '/#who', id: 'who', label: 'Who it’s for' },
];

/** Tracks which homepage section sits in the upper reading band (scrollspy). */
function useActiveSection(enabled: boolean): string | null {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) { setActive(null); return; }
    const els = links
      .map((l) => document.getElementById(l.id))
      .filter((e): e is HTMLElement => !!e);
    if (!els.length) return;
    // Active = the last section whose top has passed the reading line (~35% down).
    // Measured on scroll, so the result never depends on list order.
    let frame = 0;
    const update = () => {
      frame = 0;
      const line = window.innerHeight * 0.35;
      let current: string | null = null;
      for (const el of els) {
        const r = el.getBoundingClientRect();
        if (r.top <= line && r.bottom > 64) current = el.id;
      }
      setActive(current);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    const obs = { disconnect: () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    } };
    return () => obs.disconnect();
  }, [enabled]);
  return active;
}

const desktopLink = (active: boolean) =>
  cn(
    'relative text-sm transition-colors hover:text-background after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-full after:rounded-full after:bg-brand after:transition-opacity',
    active ? 'text-background after:opacity-100' : 'text-background/70 after:opacity-0',
  );
const mobileLink = (active: boolean) =>
  cn(
    'rounded-md px-3 py-3 text-base transition-colors hover:bg-white/10 hover:text-background',
    active ? 'bg-white/10 font-medium text-background' : 'text-background/80',
  );

// Warm the route chunks so header clicks swap content without a loading flash.
const prefetchPricing = () => { void import('@/pages/Pricing'); };
const prefetchSupport = () => { void import('@/pages/Support'); };

/** Slim marketing header. Auth lives on /auth — this only points at it. */
const MarketingHeader = () => {
  const [open, setOpen] = useState(false);
  const { pathname, search } = useLocation();
  const activeSection = useActiveSection(pathname === '/');
  const onPricing = pathname === '/pricing';
  const onDemo = pathname === '/support' && new URLSearchParams(search).get('section') === 'demo';

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-foreground/95 backdrop-blur supports-[backdrop-filter]:bg-foreground/80">
      <div className="container-wide flex h-16 items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2" aria-label="Cannafax home">
          <img src={logo} alt="Cannafax" className="h-7 w-auto brightness-0 invert" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 md:flex" aria-label="Homepage sections">
          {links.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              className={desktopLink(activeSection === l.id)}
              aria-current={activeSection === l.id ? 'location' : undefined}
            >
              {l.label}
            </Link>
          ))}
          {/* Pricing is its own page, so it gets a route link rather than an anchor. */}
          <Link
            to="/pricing"
            onMouseEnter={prefetchPricing}
            onFocus={prefetchPricing}
            className={desktopLink(onPricing)}
            aria-current={onPricing ? 'page' : undefined}
          >
            Pricing
          </Link>
          <Link
            to="/support?section=demo"
            onMouseEnter={prefetchSupport}
            onFocus={prefetchSupport}
            className={desktopLink(onDemo)}
            aria-current={onDemo ? 'page' : undefined}
          >
            Book a demo
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm" className="text-background hover:bg-white/10 hover:text-background">
            <Link to="/auth">Sign in</Link>
          </Button>
          {/* Primary CTA stays visible at every breakpoint — never hide the money button. */}
          <Button asChild size="sm" className="bg-brand text-brand-foreground hover:bg-brand/90 hidden sm:inline-flex">
            <Link to="/auth?mode=signup">Get Started</Link>
          </Button>

          {/* Mobile hamburger: reveals the section nav + Pricing + auth CTAs. */}
          <Sheet open={open} onOpenChange={setOpen}>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-background hover:bg-white/10 hover:text-background"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <SheetContent
              side="right"
              className="w-[280px] bg-foreground text-background border-white/10 p-0"
            >
              <div className="flex h-full flex-col">
                <div className="flex h-16 items-center justify-between border-b border-white/10 px-6">
                  <img src={logo} alt="Cannafax" className="h-7 w-auto brightness-0 invert" />
                  <SheetClose asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-background hover:bg-white/10 hover:text-background"
                      aria-label="Close menu"
                    >
                      <span className="text-2xl leading-none">×</span>
                    </Button>
                  </SheetClose>
                </div>

                <nav className="flex flex-col gap-1 px-6 py-4" aria-label="Homepage sections">
                  {links.map((l) => (
                    <Link
                      key={l.href}
                      to={l.href}
                      onClick={() => setOpen(false)}
                      className={mobileLink(activeSection === l.id)}
                    >
                      {l.label}
                    </Link>
                  ))}
                  <Link
                    to="/pricing"
                    onClick={() => setOpen(false)}
                    className={mobileLink(onPricing)}
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/support?section=demo"
                    onClick={() => setOpen(false)}
                    className={mobileLink(onDemo)}
                  >
                    Book a demo
                  </Link>
                </nav>

                <div className="mt-auto border-t border-white/10 px-6 py-4">
                  <Button asChild variant="ghost" className="w-full justify-start text-background hover:bg-white/10 hover:text-background">
                    <Link to="/auth" onClick={() => setOpen(false)}>Sign in</Link>
                  </Button>
                  <Button asChild className="mt-2 w-full bg-brand text-brand-foreground hover:bg-brand/90">
                    <Link to="/auth?mode=signup" onClick={() => setOpen(false)}>Get Started</Link>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default MarketingHeader;
